import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { observer } from 'mobx-react-lite';
import { SearchBoxComponent } from '../../Components/Search/SearchBoxComponent';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import SelectDropdown from '../../Components/Dropdown/SelectDropdown';
import SidePanel from '../../Components/SidePanel/SidePanel';
import { InputCheckbox } from '../../Components/Input/InputCheckbox';
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    PlusIcon,
    DocumentDuplicateIcon,
    TrashIcon,
    ArrowRightEndOnRectangleIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from '@heroicons/react/20/solid';
import { SidePanelEditor } from '../../Components/SidePanelEditor/SidePanelEditor';

export const ListView = observer(({ viewModel }) => {
    const [newItemText, setNewItemText] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);
    const [collapsedItems, setCollapsedItems] = useState(new Set());
    const newItemInputRef = useRef(null);
    const listContainerRef = useRef(null);
    const itemRefs = useRef({});
    
    // Side panel state
    const [selectedItem, setSelectedItem] = useState(null);
    const [sidePanelOpen, setSidePanelOpen] = useState(false);
    const sidePanelWidth = "min(38%, 42rem)"; // 42rem = max-w-2xl, using min() to enforce max width
    
    // Calculate adjusted width for content inside the panel
    const calculateAdjustedWidth = () => {
        // Convert width to a value we can work with
        let numericWidth;
        let unit;
        
        if (sidePanelWidth.endsWith('%')) {
            numericWidth = parseFloat(sidePanelWidth);
            unit = '%';
        } else if (sidePanelWidth.endsWith('px')) {
            numericWidth = parseFloat(sidePanelWidth);
            unit = 'px';
        } else if (sidePanelWidth.endsWith('rem')) {
            numericWidth = parseFloat(sidePanelWidth);
            unit = 'rem';
        } else {
            // Default to pixels if no unit specified
            numericWidth = parseFloat(sidePanelWidth);
            unit = 'px';
        }
        
        // Subtract 2px for border and 1rem (16px) for margin equivalent to m-4
        if (unit === 'px') {
            return `${numericWidth - 2 - 16*2}px`;
        } else if (unit === 'rem') {
            return `${numericWidth - 0.125 - 1*2}rem`; // 0.125rem = 2px, 1rem = m-4
        } else {
            // For percentage, we can't directly subtract pixels
            // This is an approximation
            return `calc(${sidePanelWidth} - 2px - 2rem)`;
        }
    };
    
    const adjustedPanelWidth = calculateAdjustedWidth();
    
    // Track the current sheet to detect sheet changes
    const [currentSheetId, setCurrentSheetId] = useState(null);

    // Add this state to track the current edited text
    const [editingText, setEditingText] = useState('');
    // Add this state to track newly created items that should be focused
    const [newlyCreatedItemId, setNewlyCreatedItemId] = useState(null);

    // Add state for context menu
    const [contextMenu, setContextMenu] = useState({
        visible: false,
        x: 0,
        y: 0,
        item: null
    });

    // Add this near the top of your component
    const [isProcessing, setIsProcessing] = useState(false);

    // Ensure list container is focused initially and after certain operations
    useEffect(() => {
        // Remove auto-focus on initial render to prevent scrolling
        // listContainerRef.current?.focus();
    }, []);

    useEffect(() => {
        viewModel.initializeSheet();
        
        // Reset state when sheet changes
        const sheetId = viewModel.sheetDataStore?.currentSheet?.id;
        if (sheetId !== currentSheetId) {
            setCurrentSheetId(sheetId);
            setNewItemText('');
            setEditingId(null);
            setSidePanelOpen(false);
            setSelectedItem(null);
        }
    }, [viewModel, viewModel.sheetDataStore?.currentSheet, currentSheetId]);

    // Add to undo stack for tracking changes
    const addToUndoStack = (action) => {
        setUndoStack(prev => [...prev, action]);
        setRedoStack([]); // Clear redo stack on new action
    };

    const handleNewItemKeyDown = async (e) => {
        if (e.key === 'Enter') {
            // Only proceed if there's actual text content
            if (newItemText.trim()) {
                await viewModel.addItem(newItemText.trim());
                setNewItemText('');
                
                // Keep focus in the new item input
                setTimeout(() => {
                    newItemInputRef.current?.focus();
                }, 0);
            } else {
                // If text is empty, just clear the input but don't add an item
                setNewItemText('');
            }
        }
    };

    const handleNewItemFocus = () => {
        // When focusing the new item input, exit editing mode
        setEditingId(null);
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) return;
        
        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;
        
        if (sourceIndex === destinationIndex) return;
        
        const draggedId = result.draggableId;
        
        try {
            // Show loading state
            setIsProcessing(true);
            
            // Simply move the item to the new position
            // No need to check outline levels or block any moves
            await viewModel.moveItemToPosition(draggedId, destinationIndex);
            
        } catch (error) {
            console.error('Error during drag and drop:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    // *** DEFINE focusListContainer FIRST ***
    const focusListContainer = useCallback(() => {
        // Delay slightly to ensure DOM is ready after potential state updates
        setTimeout(() => listContainerRef.current?.focus(), 0);
    }, []); // No dependencies needed here as it only uses the ref

    // *** THEN define handleItemClick which USES focusListContainer ***
    const handleItemClick = useCallback((item, e) => {
        if (e.target.closest('button, input, a, .stopPropagation')) {
            return;
        }
        setSelectedItem(item);
        setSidePanelOpen(true);
        // Ensure the list container gets focus after clicking an item
        focusListContainer(); 
    }, [focusListContainer]); // Dependency on focusListContainer is correct

    // Remove or simplify the openSidePanel function since we're now opening the panel on click
    const openSidePanel = (item, e) => {
        e.stopPropagation();
        setSelectedItem(item);
        setSidePanelOpen(true);
    };

    // Update the handleItemDoubleClick function to start editing
    const handleItemDoubleClick = (itemId, emptyText = false) => {
        // Set the editing state to this item
        setEditingId(itemId);
        
        // Find the current item to save for potential undo
        const currentItem = viewModel.filteredItems.find(item => item.id === itemId);
        if (currentItem) {
            // If emptyText is true, start with empty text, otherwise use the current item's text
            setEditingText(emptyText ? '' : (currentItem.text || ''));
            addToUndoStack({
                type: 'edit',
                id: itemId,
                prevText: currentItem.text
            });
        }
    };

    // Completely rewrite the handleItemTextUpdate function based on TableCell approach
    const handleItemTextUpdate = async (itemId, newText) => {
        try {
            // Check if the text is empty or just whitespace
            if (!newText.trim()) {
                return;
            }
            
            setEditingId(null);
            
            setTimeout(async () => {
                try {
                    await viewModel.updateItemText(itemId, newText);
                } catch (error) {
                    // Keep error handling but remove console.error
                }
            }, 0);
        } catch (error) {
            // Keep error handling but remove console.error
        }
    };

    // Completely rewrite the toggle function to fix the bugs
    const toggleCollapseLevel = (expand) => {
        // Get all outline levels present in the data
        const outlineLevels = viewModel.filteredItems
            .map(item => item.outlineLevel || 0)
            .filter((value, index, self) => self.indexOf(value) === index)
            .sort((a, b) => a - b);
        
        if (expand) {
            // EXPAND: Find the lowest level that has collapsed items
            // First, get all items that are currently collapsed
            const collapsedItemIds = Array.from(collapsedItems);
            const collapsedItemsData = collapsedItemIds
                .map(id => viewModel.filteredItems.find(item => item.id === id))
                .filter(Boolean);
            
            if (collapsedItemsData.length === 0) {
                // Nothing to expand, everything is already expanded
                return;
            }
            
            // Find the lowest outline level among collapsed items
            const lowestCollapsedLevel = Math.min(
                ...collapsedItemsData.map(item => item.outlineLevel || 0)
            );
            
            // Expand only items at this lowest level
            const newCollapsedItems = new Set(
                Array.from(collapsedItems).filter(id => {
                    const item = viewModel.filteredItems.find(item => item.id === id);
                    return item && (item.outlineLevel || 0) > lowestCollapsedLevel;
                })
            );
            
            setCollapsedItems(newCollapsedItems);
        } else {
            // COLLAPSE: Find the highest visible level that has items with children
            
            // First, determine which items are currently visible
            const visibleItems = viewModel.filteredItems.filter((item, index) => 
                isItemVisible(item, viewModel.filteredItems, index)
            );
            
            // Get the outline levels of visible items with children
            const visibleLevelsWithChildren = visibleItems
                .filter((item, index) => {
                    const originalIndex = viewModel.filteredItems.findIndex(i => i.id === item.id);
                    return hasChildren(item, originalIndex, viewModel.filteredItems);
                })
                .map(item => item.outlineLevel || 0)
                .filter((value, index, self) => self.indexOf(value) === index)
                .sort((a, b) => b - a); // Sort in descending order
            
            if (visibleLevelsWithChildren.length === 0) {
                // No visible items with children to collapse
                return;
            }
            
            // Get the highest level that has items with children
            const highestVisibleLevel = visibleLevelsWithChildren[0];
            
            // Collapse all items at this level that have children
            const itemsToCollapse = viewModel.filteredItems
                .filter((item, index) => 
                    (item.outlineLevel || 0) === highestVisibleLevel && 
                    hasChildren(item, index, viewModel.filteredItems) &&
                    !collapsedItems.has(item.id)
                )
                .map(item => item.id);
            
            if (itemsToCollapse.length === 0) {
                // If no items at this level to collapse, try the next level down
                if (visibleLevelsWithChildren.length > 1) {
                    const nextLevel = visibleLevelsWithChildren[1];
                    const nextItemsToCollapse = viewModel.filteredItems
                        .filter((item, index) => 
                            (item.outlineLevel || 0) === nextLevel && 
                            hasChildren(item, index, viewModel.filteredItems) &&
                            !collapsedItems.has(item.id)
                        )
                        .map(item => item.id);
                    
                    setCollapsedItems(new Set([...Array.from(collapsedItems), ...nextItemsToCollapse]));
                }
            } else {
                setCollapsedItems(new Set([...Array.from(collapsedItems), ...itemsToCollapse]));
            }
        }
    };

    // Function to determine if an item should be visible
    const isItemVisible = (item, items, index) => {
        // Always show items with outline level 0
        if (item.outlineLevel === 0) return true;
        
        // Look backwards to find parent
        for (let i = index - 1; i >= 0; i--) {
            const potentialParent = items[i];
            
            // If we find an item with same or lower level, it's the parent or sibling
            if (potentialParent.outlineLevel < item.outlineLevel) {
                // If parent is collapsed, hide this item
                if (collapsedItems.has(potentialParent.id)) {
                    return false;
                }
                
                // If parent is visible, check if parent's parent is visible
                return isItemVisible(potentialParent, items, i);
            } else if (potentialParent.outlineLevel === item.outlineLevel) {
                // Found a sibling, so check if sibling's parent is visible
                return isItemVisible(potentialParent, items, i);
            }
        }
        
        // If no parent found, item is at top level
        return true;
    };

    // Function to check if an item has children
    const hasChildren = (item, index, items) => {
        if (index === items.length - 1) return false;
        return items[index + 1].outlineLevel > item.outlineLevel;
    };

    // Update the useEffect to handle focusing on newly created items
    useEffect(() => {
        // If we have a newly created item, focus and edit it with empty text
        if (newlyCreatedItemId && viewModel.filteredItems.some(item => item.id === newlyCreatedItemId)) {
            handleItemDoubleClick(newlyCreatedItemId, true); // Pass true to start with empty text
            setNewlyCreatedItemId(null);
            
            // Prevent scrolling when focusing on newly created items
            window.scrollTo(0, 0);
        }
    }, [viewModel.filteredItems, newlyCreatedItemId]);

    // Add handler for right-click context menu
    const handleContextMenu = (e, item) => {
        e.preventDefault();
        e.stopPropagation();
        
        setContextMenu({
            visible: true,
            x: e.clientX,
            y: e.clientY - 20,
            item: item
        });
    };
    
    // Add handler to close context menu
    const handleCloseContextMenu = () => {
        setContextMenu({
            visible: false,
            x: 0,
            y: 0,
            item: null
        });
    };
    
    // Add effect to close context menu on any click outside
    useEffect(() => {
        if (contextMenu.visible) {
            const handleClickOutside = () => {
                handleCloseContextMenu();
            };
            
            const timeoutId = setTimeout(() => {
                document.addEventListener('click', handleClickOutside);
            }, 100);
            
            return () => {
                clearTimeout(timeoutId);
                document.removeEventListener('click', handleClickOutside);
            };
        }
    }, [contextMenu.visible]);

    // Create a custom component for the context menu to ensure it opens immediately
    const ContextMenu = () => {
        // Force the dropdown to be open immediately
        useEffect(() => {
            // This is a hack to trigger a click on the dropdown to open it
            const timer = setTimeout(() => {
                const dropdownTrigger = document.querySelector('.context-menu-trigger');
                if (dropdownTrigger) {
                    dropdownTrigger.click();
                }
            }, 0);
            
            return () => clearTimeout(timer);
        }, []);
        
        return (
            <div 
                className="fixed z-[9999]" 
                style={{ 
                    top: contextMenu.y,
                    left: contextMenu.x
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <SelectDropdown
                    options={[
                        {
                            label: "Decrease indent",
                            icon: <ArrowLeftIcon className="h-4 w-4" />,
                            value: "outdent",
                            disabled: !contextMenu.item.outlineLevel
                        },
                        {
                            label: "Increase indent",
                            icon: <ArrowRightIcon className="h-4 w-4" />,
                            value: "indent",
                            disabled: contextMenu.item.outlineLevel >= 7
                        },
                        { type: "divider" },
                        {
                            label: "Add item above",
                            icon: <PlusIcon className="h-4 w-4 rotate-90" />,
                            value: "addAbove",
                            disabled: !!viewModel.searchTerm
                        },
                        {
                            label: "Add item below",
                            icon: <PlusIcon className="h-4 w-4 -rotate-90" />,
                            value: "addBelow",
                            disabled: !!viewModel.searchTerm
                        },
                        { type: "divider" },
                        {
                            label: "Duplicate item",
                            icon: <DocumentDuplicateIcon className="h-4 w-4" />,
                            value: "duplicate",
                            disabled: !!viewModel.searchTerm
                        },
                        { type: "divider" },
                        {
                            label: "Delete item",
                            icon: <TrashIcon className="h-4 w-4" />,
                            value: "delete",
                            destructive: true,
                            disabled: !!viewModel.searchTerm
                        }
                    ]}
                    onChange={async (action) => {
                        switch (action) {
                            case "outdent":
                                await viewModel.outdentItem(contextMenu.item.id);
                                break;
                            case "indent":
                                await viewModel.indentItem(contextMenu.item.id);
                                break;
                            case "addAbove":
                                const aboveItemId = await viewModel.addItemAbove(contextMenu.item.id);
                                setNewlyCreatedItemId(aboveItemId);
                                break;
                            case "addBelow":
                                const belowItemId = await viewModel.addItemBelow(contextMenu.item.id);
                                setNewlyCreatedItemId(belowItemId);
                                break;
                            case "duplicate":
                                await viewModel.duplicateItem(contextMenu.item.id);
                                break;
                            case "delete":
                                await viewModel.deleteItem(contextMenu.item.id);
                                break;
                            default:
                                break;
                        }
                        handleCloseContextMenu();
                    }}
                    customTrigger={
                        <div className="context-menu-trigger hidden">Trigger</div>
                    }
                />
            </div>
        );
    };

    // Update this effect to initialize collapsed items when data is loaded
    useEffect(() => {
        if (viewModel.filteredItems && viewModel.filteredItems.length > 0) {
            // Initialize with an empty set - no items collapsed by default
            setCollapsedItems(new Set());
        }
    }, [viewModel.sheetDataStore?.currentSheet]); // Only run when the sheet changes

    // Recalculate visible items whenever dependencies change
    const visibleItems = useMemo(() => {
        // Ensure isItemVisible is defined before this point
        return viewModel.filteredItems.filter((item, index) => 
            isItemVisible(item, viewModel.filteredItems, index)
        );
    }, [viewModel.filteredItems, collapsedItems]); // Dependencies: run when items or collapsed state change

    // *** THEN define handleListKeyDown ***
    const handleListKeyDown = useCallback((e) => {
        console.log("List KeyDown Event:", e.key, "Target:", e.target, "Current Target:", e.currentTarget); 

        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
            console.log("Ignoring key:", e.key);
            return; 
        }

        e.preventDefault(); 
        console.log("preventDefault called for:", e.key);

        if (!visibleItems.length) {
            console.log("No visible items to navigate.");
            return; 
        }

        let currentSelectedIndex = -1;
        if (selectedItem) {
            currentSelectedIndex = visibleItems.findIndex(item => item.id === selectedItem.id);
        }
        console.log("Current Index:", currentSelectedIndex);

        let nextIndex = -1;

        if (e.key === 'ArrowDown') {
            if (currentSelectedIndex === -1) {
                nextIndex = 0; 
            } else {
                nextIndex = Math.min(currentSelectedIndex + 1, visibleItems.length - 1);
            }
        } else { // ArrowUp
            if (currentSelectedIndex === -1) {
                nextIndex = visibleItems.length - 1; 
            } else {
                nextIndex = Math.max(currentSelectedIndex - 1, 0);
            }
        }
        console.log("Next Index:", nextIndex);

        if (nextIndex !== -1 && nextIndex !== currentSelectedIndex) {
            const nextSelectedItem = visibleItems[nextIndex];
            console.log("Selecting next item:", nextSelectedItem?.id); 
            setSelectedItem(nextSelectedItem);
            // Side panel logic remains the same
            if (sidePanelOpen && nextSelectedItem) { } 
            // else if (!sidePanelOpen && nextSelectedItem) { setSidePanelOpen(true); }
        } else {
            console.log("No selection change.");
        }
    }, [visibleItems, selectedItem, sidePanelOpen]); // Dependencies are correct

    // *** THEN define useEffect hooks that might use the functions above ***
    // Focus the container when the component mounts or sheet changes
    useEffect(() => {
        focusListContainer();
    }, [viewModel.sheetDataStore?.currentSheet, focusListContainer]); // Dependency on focusListContainer is correct

    // Effect to scroll the selected item into view
    useEffect(() => {
        if (selectedItem && itemRefs.current[selectedItem.id]) {
            const element = itemRefs.current[selectedItem.id];
            if (element.offsetParent !== null) { 
                 element.scrollIntoView({
                    behavior: 'smooth', 
                    block: 'nearest',   
                 });
            }
        }
    }, [selectedItem]); 

    return (
        <div className="flex h-screen overflow-hidden">
            {/* List View Content Area */}
            <div className="flex-1 overflow-auto relative transition-all duration-300 ease-in-out">
                <div className="w-full flex flex-row relative justify-center">
                    <div className="flex w-full justify-center focus:outline-none">
                        <div className="flex flex-col p-4 w-full h-[calc(100vh-137px)] overflow-y-auto focus:outline-none">
                            <div className="flex items-center mb-4">
                                <div className="flex-grow">
                                    <SearchBoxComponent
                                        value={viewModel.searchTerm}
                                        onChange={viewModel.setSearchTerm}
                                        placeholder="Search items..."
                                    />
                                </div>
                                <div className="flex ml-2">
                                    <button
                                        className="p-2 text-gray-600 hover:text-blue-600 rounded-l-md border border-gray-300 hover:border-blue-400 flex items-center"
                                        onClick={() => toggleCollapseLevel(false)}
                                        title="Collapse one level"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                    <button
                                        className="p-2 text-gray-600 hover:text-blue-600 rounded-r-md border-t border-r border-b border-gray-300 hover:border-blue-400 flex items-center"
                                        onClick={() => toggleCollapseLevel(true)}
                                        title="Expand one level"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            
                            <DragDropContext onDragEnd={handleDragEnd}>
                                <Droppable droppableId="list-droppable">
                                    {(provided) => (
                                        <div
                                            ref={(el) => {
                                                provided.innerRef(el);
                                                listContainerRef.current = el; 
                                            }}
                                            {...provided.droppableProps}
                                            className="list-container flex-grow flex flex-col items-center focus:outline-none" 
                                            tabIndex={0}
                                            onKeyDown={handleListKeyDown}
                                            role="listbox" 
                                            aria-label="List items"
                                        >
                                            {viewModel.filteredItems.map((item, index) => { 
                                                const originalIndex = index; 
                                                const hasChildItems = hasChildren(item, originalIndex, viewModel.filteredItems);
                                                const isCollapsed = collapsedItems.has(item.id);
                                                const shouldBeVisible = isItemVisible(item, viewModel.filteredItems, originalIndex);

                                                return (
                                                    <Draggable 
                                                        key={item.id} 
                                                        draggableId={item.id} 
                                                        index={originalIndex} 
                                                        isDragDisabled={!!viewModel.searchTerm}
                                                    >
                                                        {(providedDraggable, snapshot) => {
                                                            // Define styles for animation
                                                            const baseStyle = {
                                                                overflow: 'hidden',
                                                                transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out, margin-top 0.3s ease-in-out, margin-bottom 0.3s ease-in-out, padding-top 0.3s ease-in-out, padding-bottom 0.3s ease-in-out, border-width 0.3s ease-in-out',
                                                                marginLeft: `${item.outlineLevel * 30}px`,
                                                                ...providedDraggable.draggableProps.style, // Combine with dnd styles
                                                            };

                                                            const collapsedStyle = {
                                                                maxHeight: '0px',
                                                                opacity: 0,
                                                                marginTop: '0px',
                                                                marginBottom: '0px',
                                                                paddingTop: '0px', // Override Tailwind p-1.5
                                                                paddingBottom: '0px', // Override Tailwind p-1.5
                                                                borderWidth: '0px', // Override Tailwind border-b
                                                                pointerEvents: 'none', // Prevent interaction when hidden
                                                            };

                                                            const visibleStyle = {
                                                                maxHeight: '500px', // Set a max-height large enough for content
                                                                opacity: 1,
                                                                // Tailwind classes will handle visible margins/padding/borders
                                                            };

                                                            // Combine styles based on visibility
                                                            const combinedStyle = {
                                                                ...baseStyle,
                                                                ...(shouldBeVisible ? visibleStyle : collapsedStyle),
                                                            };

                                                            return (
                                                                <div
                                                                    ref={(el) => {
                                                                        providedDraggable.innerRef(el); 
                                                                        itemRefs.current[item.id] = el; 
                                                                    }}
                                                                    {...providedDraggable.draggableProps}
                                                                    data-item-id={item.id}
                                                                    className={`flex items-center p-1.5 w-full rounded transition-colors border-b border-gray-100 
                                                                        ${snapshot.isDragging ? 'bg-blue-50 shadow-md' : ''}
                                                                        ${selectedItem?.id === item.id ? 'bg-blue-50' : ''}
                                                                        ${shouldBeVisible ? 'hover:bg-gray-50 cursor-pointer group self-center' : ''} `} 
                                                                    style={combinedStyle} 
                                                                    onClick={shouldBeVisible ? (e) => handleItemClick(item, e) : undefined} 
                                                                    onDoubleClick={shouldBeVisible ? () => handleItemDoubleClick(item.id) : undefined}
                                                                    onContextMenu={shouldBeVisible ? (e) => handleContextMenu(e, item) : undefined}
                                                                    role="option"
                                                                    aria-selected={selectedItem?.id === item.id}
                                                                >
                                                                    {!viewModel.searchTerm && (
                                                                        <div className="flex items-center">
                                                                            <div 
                                                                                {...providedDraggable.dragHandleProps} 
                                                                                className="text-gray-400 hover:text-gray-600 cursor-move mr-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                                onClick={(e) => e.stopPropagation()} 
                                                                            >
                                                                                ⋮⋮
                                                                            </div>
                                                                            {(item.outlineLevel || 0) < 7 && (
                                                                                <button
                                                                                    className="text-gray-400 hover:text-blue-600 mr-2 w-4 h-4 flex items-center justify-center stopPropagation opacity-0 group-hover:opacity-100 transition-opacity"
                                                                                    onClick={async (e) => { 
                                                                                        e.stopPropagation();
                                                                                        
                                                                                        // If the parent is collapsed, expand it first
                                                                                        if (collapsedItems.has(item.id)) {
                                                                                            setCollapsedItems(prev => {
                                                                                                const newCollapsed = new Set(prev);
                                                                                                newCollapsed.delete(item.id);
                                                                                                return newCollapsed;
                                                                                            });
                                                                                        }
                                                                                        
                                                                                        // Then add the child item (await is now valid)
                                                                                        const childItemId = await viewModel.addChildItem(item.id);
                                                                                        setNewlyCreatedItemId(childItemId);
                                                                                    }}
                                                                                    title="Add child item"
                                                                                >
                                                                                    +
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {hasChildItems && (
                                                                        <div 
                                                                            className="mr-2 w-4 h-4 text-xs flex items-center justify-center cursor-pointer text-gray-400 hover:text-gray-700"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                // Toggle collapse state for this specific item
                                                                                setCollapsedItems(prev => {
                                                                                    const newCollapsed = new Set(prev);
                                                                                    if (isCollapsed) {
                                                                                        newCollapsed.delete(item.id);
                                                                                    } else {
                                                                                        newCollapsed.add(item.id);
                                                                                    }
                                                                                    return newCollapsed;
                                                                                });
                                                                            }}
                                                                            title={isCollapsed ? "Expand" : "Collapse"}
                                                                        >
                                                                            {isCollapsed ? '▶' : '▼'}
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {!hasChildItems && (
                                                                        <div className="mr-2 w-4 h-4"></div> // Placeholder for alignment
                                                                    )}
                                                                    
                                                                    {item.done !== null && (
                                                                        <div 
                                                                            className="mr-2 stopPropagation"
                                                                            onClick={(e) => e.stopPropagation()} // Prevent click through
                                                                        >
                                                                            <InputCheckbox
                                                                                checked={item.done}
                                                                                onChange={() => viewModel.toggleItem(item.id)}
                                                                                isSelected={selectedItem?.id === item.id}
                                                                                enableKeyboardToggle={false}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {editingId === item.id ? (
                                                                        <input
                                                                            type="text"
                                                                            value={editingText}
                                                                            onChange={(e) => {
                                                                                setEditingText(e.target.value);
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                handleItemTextUpdate(item.id, editingText);
                                                                            }}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') {
                                                                                    e.preventDefault();
                                                                                    handleItemTextUpdate(item.id, editingText);
                                                                                } else if (e.key === 'Escape') {
                                                                                    e.preventDefault();
                                                                                    setEditingId(null);
                                                                                    listContainerRef.current?.focus();
                                                                                }
                                                                            }}
                                                                            autoFocus
                                                                            className="flex-grow text-sm outline-none focus:ring-0 bg-transparent" // Ensure bg is transparent
                                                                        />
                                                                    ) : (
                                                                        <div className="flex-grow">
                                                                            <ItemText 
                                                                                text={item.text} 
                                                                                done={item.done}
                                                                                state={item.state}
                                                                                dueDate={item.dueDate}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    
                                                                    <button
                                                                        className="ml-1 p-1 text-gray-400 hover:text-blue-600 rounded focus:outline-none stopPropagation opacity-0 group-hover:opacity-100 transition-opacity"
                                                                        onClick={(e) => openSidePanel(item, e)}
                                                                        title="View item details"
                                                                    >
                                                                        <ArrowRightEndOnRectangleIcon className="h-5 w-5" />
                                                                    </button>
                                                                </div> 
                                                            );
                                                        }}
                                                    </Draggable>
                                                );
                                            })}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>

                            <div className="mt-4">
                                <input
                                    ref={newItemInputRef}
                                    value={newItemText}
                                    onChange={(e) => setNewItemText(e.target.value)}
                                    onKeyDown={handleNewItemKeyDown}
                                    onFocus={handleNewItemFocus}
                                    placeholder="Add new item..."
                                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-300"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Side Panel Wrapper - Handles width animation and pushing */}
            <div
                style={{
                    // Animate width: 0 when closed, sidePanelWidth when open
                    width: sidePanelOpen ? sidePanelWidth : '0px',
                    // Transition for the width animation
                    transition: `width 300ms ease-in-out`, // Use your desired duration/easing
                    overflow: 'hidden', // CRITICAL: Clips the SidePanel content
                    flexShrink: 0, // Prevent this wrapper from shrinking
                    height: '100vh', // Match viewport height
                    position: 'relative', // Needed if SidePanel uses absolute positioning internally
                    // Optional: Add a subtle border or background if needed
                    // borderLeft: '1px solid #e5e7eb', // Example border
                }}
            >
                {/* Render SidePanel INSIDE the wrapper */}
                {/* Pass the final width explicitly */}
                <SidePanel
                    isOpen={sidePanelOpen}
                    onClose={() => {
                        setSidePanelOpen(false);
                        // Optionally reset selected item if needed
                        // setSelectedItem(null);
                    }}
                    title={selectedItem ? `${selectedItem.text}` : "Item Details"}
                    width={sidePanelWidth} // Pass the FINAL width the panel should occupy
                    animationDuration={300} // Match transition duration
                    // Ensure SidePanel doesn't use fixed positioning anymore
                    // position="right" // Keep if SidePanel uses it for internal styling (like border)
                >
                    {/* Content for the SidePanel */}
                    {selectedItem ? (
                        // Removed p-8, let SidePanelEditor handle its padding
                        <div className={`h-full overflow-y-auto flex justify-center bg-white`}>
                             <div className="space-y-4 w-full"> {/* Let content take full width */}
                                <SidePanelEditor
                                    viewModel={viewModel}
                                    itemId={selectedItem.id}
                                    // adjustedWidth might not be needed or needs recalculation
                                    onUpdateComplete={() => {}}
                                />
                                {/* Action bar - ensure it fits within panel width */}
                                <div className="bg-gray-50 border-t p-4 sticky bottom-0">
                                     <SelectDropdown
                                        options={[
                                            { 
                                                label: "Outdent",
                                                icon: <ChevronLeftIcon className="h-4 w-4" />,
                                                value: "outdent",
                                                disabled: !selectedItem.level
                                            },
                                            { 
                                                label: "Indent",
                                                icon: <ChevronRightIcon className="h-4 w-4" />,
                                                value: "indent",
                                                disabled: selectedItem.level >= 8
                                            },
                                            { type: "divider" },
                                            { 
                                                label: "Add item above",
                                                icon: <PlusIcon className="h-4 w-4" />,
                                                value: "addAbove"
                                            },
                                            { 
                                                label: "Add item below",
                                                icon: <PlusIcon className="h-4 w-4" />,
                                                value: "addBelow"
                                            },
                                            { 
                                                label: "Duplicate item",
                                                icon: <DocumentDuplicateIcon className="h-4 w-4" />,
                                                value: "duplicate"
                                            },
                                            { type: "divider" },
                                            { 
                                                label: "Delete item",
                                                icon: <TrashIcon className="h-4 w-4" />,
                                                value: "delete",
                                                destructive: true
                                            }
                                        ]}
                                        onChange={async (action) => {
                                            switch (action) {
                                                case "outdent":
                                                    await viewModel.outdentItem(selectedItem.id);
                                                    break;
                                                case "indent":
                                                    await viewModel.indentItem(selectedItem.id);
                                                    break;
                                                case "addAbove":
                                                    const aboveItemId = await viewModel.addItemAbove(selectedItem.id);
                                                    setNewlyCreatedItemId(aboveItemId);
                                                    setSidePanelOpen(false);
                                                    break;
                                                case "addBelow":
                                                    const belowItemId = await viewModel.addItemBelow(selectedItem.id);
                                                    setNewlyCreatedItemId(belowItemId);
                                                    setSidePanelOpen(false);
                                                    break;
                                                case "duplicate":
                                                    await viewModel.duplicateItem(selectedItem.id);
                                                    break;
                                                case "delete":
                                                    await viewModel.deleteItem(selectedItem.id);
                                                    setSidePanelOpen(false);
                                                    break;
                                                default:
                                                    break;
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                         <div className="p-4 text-center text-gray-500 bg-white h-full flex items-center justify-center">
                            <p>Select an item to view and edit its details.</p>
                        </div>
                    )}
                </SidePanel>
            </div> {/* End Side Panel Wrapper */}

            {/* Add context menu */}
            {contextMenu.visible && contextMenu.item && ReactDOM.createPortal(
                <ContextMenu />,
                document.body
            )}

            {/* Add this to your JSX, perhaps right after the DragDropContext */}
            {isProcessing && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                    <div className="bg-white p-4 rounded-lg shadow-lg">
                        <div className="flex items-center space-x-3">
                            <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Processing...</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

// Add this component at the end of your file, outside the main component
const ItemText = React.memo(({ text, done, state, dueDate }) => {
    // Ensure text is a string
    const displayText = typeof text === 'string' ? text : '';
    
    return (
        <>
            {done ? (
                <span className="text-sm line-through text-gray-500">{displayText}</span>
            ) : (
                <span className="text-sm">{displayText}</span>
            )}
            
            {state && (
                <span className="ml-2 text-sm text-gray-500">
                    ({state})
                </span>
            )}
            {dueDate && (
                <span className="ml-2 text-sm text-gray-500">
                    Due: {new Date(dueDate).toLocaleDateString()}
                </span>
            )}
        </>
    );
});